'use client';

import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import { JobEntity } from '@/entities/job.entity';
import type { ReviewAnswerJobSkippedEvent } from '@/entities/review-answer-job-skipped-event.entity';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReviewAnswerJobDataEntity } from '@/entities/review-answer-job-data.entity';
import { createJobsRepository } from '@/container/jobs-repository';
import { QUEUE_JOBS } from '@/constants/queue';
import { useAuth } from '@/context/auth.context';
import { useSocketIo } from '@/context/socket-io.context';
import { resolveOfferNotAcceptedMarketplaceErrorMessage } from '@/shared/errors/marketplace-api-error-message';
import { toast } from '@/ui/toast';
import { TOAST_IDS } from '@/ui/toast-ids';

const ANSWER_JOB_NAMES = [QUEUE_JOBS.createReviewAnswer, QUEUE_JOBS.updateReviewAnswer] as const;
const reviewAnswerJobsLogger = createClientLogger({
    scope: 'review-answer-jobs',
});

const createReviewAnswerJobSkippedLogContext = (event: ReviewAnswerJobSkippedEvent) => ({
    action: 'review_answer_job.skip',
    code: event.code,
    reason: event.reason,
    jobId: event.jobId,
    jobName: event.jobName,
    reviewFeedItemId: event.reviewFeedItemId,
    operationId: event.operationId,
    clientId: event.clientId,
});

type ReviewAnswerJobsContextType = {
    jobs: JobEntity<ReviewAnswerJobDataEntity>[];
    refetchActiveJobs: (authToken: string) => Promise<void>;
    cancelActiveJobs: (authToken: string) => Promise<void>;
    isCancelingActiveJobs: boolean;
    getAnswerJob: (reviewFeedItemId: string) => JobEntity<ReviewAnswerJobDataEntity> | undefined;
};

type ProviderProps = Pick<ReviewAnswerJobsContextType, 'jobs'> & {
    children: ReactNode;
};

const ReviewAnswerJobsContext = createContext<ReviewAnswerJobsContextType>({
    jobs: [],
    refetchActiveJobs: () => Promise.resolve(),
    cancelActiveJobs: () => Promise.resolve(),
    isCancelingActiveJobs: false,
    getAnswerJob: () => undefined,
});

export const useReviewAnswerJobs = () => useContext(ReviewAnswerJobsContext);

export const ReviewAnswerJobsContextProvider = ({ children, ...providerValue }: ProviderProps) => {
    const [jobs, setJobs] = useState(providerValue.jobs);
    const [isCancelingActiveJobs, setIsCancelingActiveJobs] = useState(false);
    const jobsRepository = useMemo(() => createJobsRepository(), []);
    const { events } = useSocketIo();
    const { accessToken } = useAuth();
    const prevJobsCountRef = useRef(0);
    const lastTerminalReasonRef = useRef<'completed' | 'offer_not_accepted' | null>(null);

    const readOperationId = (job: JobEntity<ReviewAnswerJobDataEntity>): string | null => {
        const operationId = job.data.operationId;
        if (typeof operationId !== 'string') {
            return null;
        }

        const trimmedOperationId = operationId.trim();
        return trimmedOperationId || null;
    };

    const fetchAndSetActiveJobs = useCallback(
        async (authToken: string): Promise<JobEntity<ReviewAnswerJobDataEntity>[]> => {
            const freshJobs = await jobsRepository.getActive<ReviewAnswerJobDataEntity>([...ANSWER_JOB_NAMES], {
                authToken,
            });

            setJobs(freshJobs.data);
            return freshJobs.data;
        },
        [jobsRepository],
    );

    const refetchActiveJobs = async (authToken: string) => {
        await fetchAndSetActiveJobs(authToken);
    };

    const cancelActiveJobs = async (authToken: string) => {
        if (isCancelingActiveJobs) {
            return;
        }
        if (!jobs.length) {
            return;
        }

        setIsCancelingActiveJobs(true);

        try {
            const operationIds = [...new Set(jobs.map(readOperationId).filter((value): value is string => !!value))];
            const hasJobsWithoutOperationId = jobs.some((job) => readOperationId(job) === null);

            for (const operationId of operationIds) {
                await jobsRepository.cancel(
                    {
                        jobNames: [...ANSWER_JOB_NAMES],
                        operationId,
                    },
                    { authToken },
                );
            }

            if (hasJobsWithoutOperationId) {
                await jobsRepository.cancel(
                    {
                        jobNames: [...ANSWER_JOB_NAMES],
                    },
                    { authToken },
                );
            }

            const remainingJobs = await fetchAndSetActiveJobs(authToken);
            if (remainingJobs.length === 0) {
                toast.success('Очередь ответов очищена');
            } else {
                toast.warning(`Не все ответы удалось отменить: осталось ${remainingJobs.length}`);
            }
        } catch (error) {
            logClientError({
                logger: reviewAnswerJobsLogger,
                message: '[client.review-answer-jobs.cancel-active] Failed to cancel active answer jobs.',
                error,
                context: {
                    activeJobCount: jobs.length,
                },
            });
            toast.error('Не удалось отменить ответы в очереди');
        } finally {
            setIsCancelingActiveJobs(false);
        }
    };

    const getAnswerJob = (reviewFeedItemId: string) => jobs.find((j) => j.data.reviewFeedItemId === reviewFeedItemId);

    useEffect(() => {
        if (prevJobsCountRef.current > 0 && jobs.length === 0) {
            if (lastTerminalReasonRef.current !== 'offer_not_accepted') {
                toast.success('Все отзывы получили ответ');
            }
            lastTerminalReasonRef.current = null;
        }
        if (jobs.length > 0) {
            lastTerminalReasonRef.current = null;
        }
        prevJobsCountRef.current = jobs.length;
    }, [jobs.length]);

    useEffect(() => {
        const unsubscribeAnswerJobDone = events.on('reviewAnswerJobDone', ({ jobId }) => {
            setJobs((currentJobs) => {
                const nextJobs = currentJobs.filter((job) => job.id !== jobId);
                if (nextJobs.length !== currentJobs.length) {
                    lastTerminalReasonRef.current = 'completed';
                }
                return nextJobs.length === currentJobs.length ? currentJobs : nextJobs;
            });
        });
        const unsubscribeAnswerJobSkipped = events.on('reviewAnswerJobSkipped', (event) => {
            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: reviewAnswerJobsLogger,
                message: '[client.review-answer-jobs.policy-skip] Answer job skipped by offer acceptance policy.',
                error: event,
                context: createReviewAnswerJobSkippedLogContext(event),
            });
            toast.warning(resolveOfferNotAcceptedMarketplaceErrorMessage(), {
                autoClose: false,
                toastId: TOAST_IDS.reviewAnswerJobsOfferNotAccepted,
            });
            setJobs((currentJobs) => {
                const nextJobs = currentJobs.filter((job) => job.id !== event.jobId);
                if (nextJobs.length !== currentJobs.length) {
                    lastTerminalReasonRef.current = event.reason;
                }
                return nextJobs.length === currentJobs.length ? currentJobs : nextJobs;
            });
        });
        const unsubscribeReviewChanged = events.on('reviewFeedItemChanged', ({ reason, reviewFeedItemId }) => {
            if (reason !== 'auto_reply_answer_queued') {
                return;
            }

            void fetchAndSetActiveJobs(accessToken).catch((error) => {
                logClientError({
                    logger: reviewAnswerJobsLogger,
                    message:
                        '[client.review-answer-jobs.auto-reply-refresh] Failed to refresh active answer jobs after auto-reply queue update.',
                    error,
                    context: {
                        reviewFeedItemId,
                    },
                });
            });
        });

        return () => {
            unsubscribeAnswerJobDone();
            unsubscribeAnswerJobSkipped();
            unsubscribeReviewChanged();
        };
    }, [accessToken, events, fetchAndSetActiveJobs]);

    return (
        <ReviewAnswerJobsContext
            value={{ jobs, refetchActiveJobs, cancelActiveJobs, isCancelingActiveJobs, getAnswerJob }}
        >
            {children}
        </ReviewAnswerJobsContext>
    );
};
